/** Study-guide quiz banks for the Divergence & Curl guide — 15 MCQs per section. */

export const FIELD_CONCEPT_QUIZ = [
  {
    prompt: "Evaluate $\\mathbf F(1,-2,3)$ for $\\mathbf F=\\langle x,y,z\\rangle$.",
    options: ["$\\langle 1,2,3\\rangle$", "$\\langle 1,-2,3\\rangle$", "$\\langle 0,0,0\\rangle$"],
    answer: "B",
    explanation: "Substitute the point into each component.",
  },
  {
    prompt: "The field $\\mathbf F=\\langle -y,x\\rangle$ in the plane is best described as:",
    options: ["A rotational (circular) field", "A constant field", "A radial source field"],
    answer: "A",
    explanation: "It is tangent to circles centered at the origin.",
  },
  {
    prompt: "For $\\mathbf G=\\langle 2,0,-1\\rangle$, the vector at every point is:",
    options: ["Zero", "Radial from the origin", "The same constant vector"],
    answer: "C",
    explanation: "No dependence on $(x,y,z)$.",
  },
  {
    prompt: "If $\\mathbf F=\\nabla f$ for $f=x^2y$, then $\\mathbf F$ equals:",
    options: ["$\\langle x^2,y\\rangle$", "$\\langle 2xy,x^2\\rangle$", "$\\langle 2x,y\\rangle$"],
    answer: "B",
    explanation: "Compute the gradient components.",
  },
  {
    prompt: "Component functions of $\\mathbf F=\\langle P,Q,R\\rangle=\\langle yz,xz,xy\\rangle$ give $P(1,2,3)$:",
    options: ["$3$", "$2$", "$6$"],
    answer: "C",
    explanation: "$P=yz=2\\cdot 3=6$.",
  },
  {
    prompt: "A flow line of a constant field $\\mathbf F=\\langle 1,0,0\\rangle$ is:",
    options: ["A straight line parallel to the $x$-axis", "A circle in the $xy$-plane", "A helix about the $z$-axis"],
    answer: "A",
    explanation: "Velocity is always $\\mathbf i$.",
  },
  {
    prompt: "The radial field $\\mathbf F=\\langle x,y\\rangle$ points:",
    options: ["Away from the origin (for $(x,y)\\ne\\mathbf 0$)", "Clockwise around the origin", "Always upward"],
    answer: "A",
    explanation: "The vector equals the position vector.",
  },
  {
    prompt: "Evaluate $\\mathbf H(0,1,0)$ for $\\mathbf H=\\langle z,x,y\\rangle$.",
    options: ["$\\langle 1,0,0\\rangle$", "$\\langle 0,0,1\\rangle$", "$\\langle 0,1,0\\rangle$"],
    answer: "B",
    explanation: "$\\langle 0,0,1\\rangle$.",
  },
  {
    prompt: "A gradient field is also called:",
    options: ["Incompressible only", "Solenoidal", "Conservative (on a suitable domain)"],
    answer: "C",
    explanation: "Gradient fields are conservative where the domain allows.",
  },
  {
    prompt: "For $\\mathbf F=\\langle e^x,0,0\\rangle$, $\\|\\mathbf F(0,5,-2)\\|$ equals:",
    options: ["$e$", "$1$", "$0$"],
    answer: "B",
    explanation: "$\\mathbf F(0,5,-2)=\\langle 1,0,0\\rangle$.",
  },
  {
    prompt: "Sketch cue: arrows of $\\mathbf F=\\langle -x,-y\\rangle$ near the origin point:",
    options: ["Away from the origin", "Tangentially clockwise", "Toward the origin"],
    answer: "C",
    explanation: "It is the inward radial field.",
  },
  {
    prompt: "If $\\mathbf F=\\langle P,Q\\rangle$ with $P=x$ and $Q=0$, streamlines satisfy:",
    options: ["Horizontal motion scaling with $x$", "Vertical circles", "Constant speed $1$ everywhere"],
    answer: "A",
    explanation: "Velocity has only an $x$-component equal to $x$.",
  },
  {
    prompt: "The zero field $\\mathbf F=\\mathbf 0$ has flow lines that are:",
    options: ["Unit-speed lines", "Only closed loops", "Constant (stationary points)"],
    answer: "C",
    explanation: "Velocity zero means particles stay put.",
  },
  {
    prompt: "For $\\mathbf F=\\langle xy,1\\rangle$, the $y$-component at $(2,3)$ is:",
    options: ["$1$", "$2$", "$6$"],
    answer: "A",
    explanation: "$Q=1$ constantly.",
  },
  {
    prompt: "A vector field on $\\mathbb{R}^3$ assigns to each point:",
    options: ["A $3\\times 3$ matrix only", "A vector in $\\mathbb{R}^3$", "A scalar only"],
    answer: "B",
    explanation: "Definition of a 3D vector field.",
  },
  {
    prompt: "A vector field $\\mathbf F = \\langle c, 0, 0 \\rangle$ where $c > 0$ represents:",
    options: ["Uniform flow along the positive $x$-axis", "Uniform flow along the $y$-axis", "A radial source"],
    answer: "A",
    explanation: "The vector is constant and points strictly in the $+x$ direction.",
  },
  {
    prompt: "The domain of $\\mathbf F(x,y) = \\langle 1/x, 1/y \\rangle$ excludes:",
    options: ["The $x$ and $y$ axes", "The origin only", "The line $y=x$"],
    answer: "A",
    explanation: "Division by zero occurs whenever $x=0$ or $y=0$.",
  },
  {
    prompt: "If $\\mathbf F = \\langle y, x, z \\rangle$, the magnitude $\\|\\mathbf F\\|$ at $(1, 1, 1)$ is:",
    options: ["$\\sqrt{3}$", "$3$", "$1$"],
    answer: "A",
    explanation: "$\\mathbf F(1,1,1) = \\langle 1,1,1 \\rangle$, whose magnitude is $\\sqrt{1^2+1^2+1^2} = \\sqrt{3}$.",
  },
  {
    prompt: "In a fluid velocity field, a streamline is a curve that is:",
    options: ["Tangent to the velocity vector at every point", "Perpendicular to the velocity vector at every point", "Always a closed loop"],
    answer: "A",
    explanation: "Streamlines visually trace the exact direction of flow at every point.",
  },
  {
    prompt: "For the gravitational force field $\\mathbf F = -\\frac{GMm}{r^3}\\mathbf r$, the field points:",
    options: ["Directly toward the origin", "Directly away from the origin", "In a circular path"],
    answer: "A",
    explanation: "The negative sign on the radial vector $\\mathbf r$ indicates it is an inward attractive force.",
  },
];

export const DIV_FORMULA_QUIZ = [
  {
    prompt: "Compute $\\nabla\\cdot\\langle x^2,y^2,z^2\\rangle$.",
    options: ["$2x+2y+2z$", "$x^2+y^2+z^2$", "$2x$"],
    answer: "A",
    explanation: "$P_x+Q_y+R_z=2x+2y+2z$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle y,-x,0\\rangle$, $\\nabla\\cdot\\mathbf F$ equals:",
    options: ["$2$", "$-2$", "$0$"],
    answer: "C",
    explanation: "$0+0+0=0$.",
  },
  {
    prompt: "Evaluate $\\nabla\\cdot\\langle e^x,e^y,e^z\\rangle$ at $(0,0,0)$.",
    options: ["$1$", "$3$", "$0$"],
    answer: "B",
    explanation: "Divergence is $e^x+e^y+e^z$; at origin equals $3$.",
  },
  {
    prompt: "If $\\nabla\\cdot\\mathbf F>0$ at a point, the field behaves locally like a:",
    options: ["Pure rotation with no expansion", "Sink", "Source"],
    answer: "C",
    explanation: "Positive divergence means net outward flux density.",
  },
  {
    prompt: "A field with $\\nabla\\cdot\\mathbf F=0$ everywhere is called:",
    options: ["Conservative", "Solenoidal (incompressible)", "Irrotational"],
    answer: "B",
    explanation: "Zero divergence is the solenoidal condition.",
  },
  {
    prompt: "Compute $\\nabla\\cdot\\langle 3x,2y,-5z\\rangle$.",
    options: ["$0$", "$-5$", "$3$"],
    answer: "A",
    explanation: "$3+2-5=0$.",
  },
  {
    prompt: "Laplacian connection: $\\nabla\\cdot(\\nabla f)$ equals:",
    options: ["$\\|\\nabla f\\|$", "$\\nabla\\times(\\nabla f)$", "$\\nabla^2 f$"],
    answer: "C",
    explanation: "Divergence of the gradient is the Laplacian.",
  },
  {
    prompt: "For $f=x^2+y^2+z^2$, $\\nabla\\cdot(\\nabla f)$ equals:",
    options: ["$0$", "$6$", "$2$"],
    answer: "B",
    explanation: "$\\nabla f=\\langle 2x,2y,2z\\rangle$, divergence $6$.",
  },
  {
    prompt: "Compute $\\nabla\\cdot\\langle xy,yz,zx\\rangle$ at $(1,1,1)$.",
    options: ["$3$", "$0$", "$1$"],
    answer: "A",
    explanation: "$y+z+x$ equals $3$ at $(1,1,1)$.",
  },
  {
    prompt: "Divergence of a constant field is:",
    options: ["$0$", "$1$", "Undefined"],
    answer: "A",
    explanation: "All partials vanish.",
  },
  {
    prompt: "For $\\mathbf F=\\langle x/r^3,y/r^3,z/r^3\\rangle$ with $r=\\sqrt{x^2+y^2+z^2}$ (off the origin), a classical computation gives $\\nabla\\cdot\\mathbf F$:",
    options: ["$1$", "$0$", "$3/r^2$"],
    answer: "B",
    explanation: "Away from the origin this Coulomb field is divergence-free.",
  },
  {
    prompt: "If $\\mathbf F=\\langle P,Q,R\\rangle$ and $P_x=1$, $Q_y=2$, $R_z=-4$, then $\\nabla\\cdot\\mathbf F$ equals:",
    options: ["$7$", "$3$", "$-1$"],
    answer: "C",
    explanation: "$1+2-4=-1$.",
  },
  {
    prompt: "Sign check: $\\nabla\\cdot\\langle -x,-y,-z\\rangle$ equals:",
    options: ["$0$", "$-3$", "$3$"],
    answer: "B",
    explanation: "Each component contributes $-1$.",
  },
  {
    prompt: "Compute $\\nabla\\cdot\\langle \\sin y,\\cos x,z\\rangle$.",
    options: ["$\\cos y-\\sin x$", "$0$", "$1$"],
    answer: "C",
    explanation: "$P_x=0$, $Q_y=0$, $R_z=1$.",
  },
  {
    prompt: "At a sink point one expects $\\nabla\\cdot\\mathbf F$ to be:",
    options: ["Negative", "Positive", "Always exactly $1$"],
    answer: "A",
    explanation: "Negative divergence means net inward flux density.",
  },
  {
    prompt: "Compute $\\nabla \\cdot \\langle x, 2y, 3z \\rangle$.",
    options: ["$6$", "$x+2y+3z$", "$\\langle 1, 2, 3 \\rangle$"],
    answer: "A",
    explanation: "$1 + 2 + 3 = 6$.",
  },
  {
    prompt: "Divergence mathematically transforms a:",
    options: ["Vector field into a scalar field", "Scalar field into a vector field", "Vector field into another vector field"],
    answer: "A",
    explanation: "The dot product of the del operator with a vector field yields a scalar function.",
  },
  {
    prompt: "For $\\mathbf F = \\langle \\sin x, \\cos y, 0 \\rangle$, $\\nabla \\cdot \\mathbf F$ is:",
    options: ["$\\cos x - \\sin y$", "$\\cos x + \\sin y$", "$0$"],
    answer: "A",
    explanation: "$P_x = \\cos x$ and $Q_y = -\\sin y$, so the sum is $\\cos x - \\sin y$.",
  },
  {
    prompt: "If a gas is undergoing uniform expansion in all directions, its velocity field's divergence is:",
    options: ["Positive everywhere", "Negative everywhere", "Zero everywhere"],
    answer: "A",
    explanation: "Expansion means a net outward flow, which corresponds to positive divergence.",
  },
  {
    prompt: "Evaluate $\\nabla \\cdot \\langle xy^2, x^2y, z^3 \\rangle$ at $(1, 1, 1)$.",
    options: ["$5$", "$3$", "$0$"],
    answer: "A",
    explanation: "$P_x = y^2$, $Q_y = x^2$, $R_z = 3z^2$. At $(1,1,1)$, $1 + 1 + 3 = 5$.",
  }
];

export const CURL_CORE_QUIZ = [
  {
    prompt: "Compute $\\nabla\\times\\langle -y,x,0\\rangle$.",
    options: ["$\\langle 0,0,0\\rangle$", "$\\langle 0,0,2\\rangle$", "$\\langle 0,0,1\\rangle$"],
    answer: "B",
    explanation: "$Q_x-P_y=1-(-1)=2$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle yz,xz,xy\\rangle$, $\\nabla\\times\\mathbf F$ equals:",
    options: ["$\\mathbf 0$", "$\\langle 1,1,1\\rangle$", "$\\langle y,x,0\\rangle$"],
    answer: "A",
    explanation: "Each curl component cancels.",
  },
  {
    prompt: "Curl of a gradient $\\nabla\\times(\\nabla f)$ is:",
    options: ["$\\nabla^2 f$", "$\\nabla f$", "$\\mathbf 0$"],
    answer: "C",
    explanation: "Classic identity.",
  },
  {
    prompt: "For a 2D field $\\langle P,Q\\rangle$, $\\nabla\\times\\mathbf F$ reduces to:",
    options: ["$(P_x+Q_y)\\mathbf k$", "$(P_y-Q_x)\\mathbf i$", "$(Q_x-P_y)\\mathbf k$"],
    answer: "C",
    explanation: "Only the $k$-component survives.",
  },
  {
    prompt: "Evaluate $\\nabla\\times\\langle 0,-z,y\\rangle$.",
    options: ["$\\langle 2,0,0\\rangle$", "$\\langle 0,1,-1\\rangle$", "$\\langle 0,0,0\\rangle$"],
    answer: "A",
    explanation: "$\\mathbf i(1-(-1))=2\\mathbf i$.",
  },
  {
    prompt: "A field with $\\nabla\\times\\mathbf F=\\mathbf 0$ is called:",
    options: ["Solenoidal", "Irrotational", "Incompressible"],
    answer: "B",
    explanation: "Zero curl means irrotational.",
  },
  {
    prompt: "Rigid rotation $\\mathbf F=\\langle -\\omega y,\\omega x,0\\rangle$ has curl:",
    options: ["$\\langle \\omega,\\omega,0\\rangle$", "$\\langle 0,0,0\\rangle$", "$\\langle 0,0,2\\omega\\rangle$"],
    answer: "C",
    explanation: "$Q_x-P_y=\\omega-(-\\omega)=2\\omega$.",
  },
  {
    prompt: "Compute $\\nabla\\times\\langle z,0,-x\\rangle$ at $(1,2,3)$.",
    options: ["$\\langle 0,2,0\\rangle$", "$\\langle 0,0,0\\rangle$", "$\\langle 1,0,-1\\rangle$"],
    answer: "A",
    explanation: "Curl is $\\langle 0,2,0\\rangle$ constantly.",
  },
  {
    prompt: "If $\\mathbf F=\\langle x^2,y^2,z^2\\rangle$, then $\\nabla\\times\\mathbf F$ equals:",
    options: ["$\\langle 2x,2y,2z\\rangle$", "$\\mathbf 0$", "$\\langle 0,0,2\\rangle$"],
    answer: "B",
    explanation: "All curl components vanish.",
  },
  {
    prompt: "Compute the $k$-component of curl for $\\langle x^2y,xy^2\\rangle$:",
    options: ["$y^2-x^2$", "$x^2-y^2$", "$2xy$"],
    answer: "A",
    explanation: "$Q_x-P_y=y^2-x^2$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle 0,0,x\\rangle$, $\\nabla\\times\\mathbf F$ equals:",
    options: ["$\\langle 1,0,0\\rangle$", "$\\langle 0,-1,0\\rangle$", "$\\mathbf 0$"],
    answer: "B",
    explanation: "$\\mathbf j$ component is $-(R_x-P_z)=-1$.",
  },
  {
    prompt: "Curl measures local:",
    options: ["Expansion density", "Path length", "Rotation density"],
    answer: "C",
    explanation: "Interpretation of $\\nabla\\times\\mathbf F$.",
  },
  {
    prompt: "Compute $\\nabla\\times\\langle ye^z,xe^z,xye^z\\rangle$.",
    options: ["$\\langle 0,0,e^z\\rangle$", "$\\langle e^z,e^z,0\\rangle$", "$\\mathbf 0$"],
    answer: "C",
    explanation: "It is $\\nabla(xye^z)$ up to checking; curl vanishes.",
  },
  {
    prompt: "If curl is $\\langle 0,0,4\\rangle$ everywhere, circulation around a unit circle (via Stokes on the disk) is:",
    options: ["$4\\pi$", "$4$", "$0$"],
    answer: "A",
    explanation: "Flux of curl through unit disk is $4\\cdot\\pi$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle -z,0,y\\rangle$, $\\nabla\\times\\mathbf F$ equals:",
    options: ["$\\langle 0,0,0\\rangle$", "$\\langle 1,1,0\\rangle$", "$\\langle 1,0,1\\rangle$"],
    answer: "B",
    explanation: "$\\mathbf i(1-0)-\\mathbf j(0-(-1))+\\mathbf k(0)=\\langle 1,1,0\\rangle$.",
  },
  {
    prompt: "$\\nabla \\times \\langle x, y, z \\rangle$ equals:",
    options: ["$\\mathbf 0$", "$\\langle 1, 1, 1 \\rangle$", "$3$"],
    answer: "A",
    explanation: "There are no mixed variables; $R_y=0, Q_z=0$, etc., so every component of the curl is zero.",
  },
  {
    prompt: "Curl mathematically transforms a:",
    options: ["Vector field into another vector field", "Vector field into a scalar field", "Scalar field into a vector field"],
    answer: "A",
    explanation: "The cross product of the del operator with a vector field yields a new vector field.",
  },
  {
    prompt: "Compute the $\\mathbf{j}$-component of $\\nabla \\times \\langle 0, xz, 0 \\rangle$.",
    options: ["$0$", "$x$", "$-z$"],
    answer: "A",
    explanation: "The $\\mathbf j$-component is $P_z - R_x$. Since $P=0$ and $R=0$, this component is $0$.",
  },
  {
    prompt: "If a tiny paddlewheel placed in a fluid flow does not spin at all, the field's curl there is:",
    options: ["$\\mathbf 0$", "Positive", "Negative"],
    answer: "A",
    explanation: "Curl measures microscopic rotation; no spin means zero curl.",
  },
  {
    prompt: "For $\\mathbf F = \\langle y^2, z^2, x^2 \\rangle$, the $\\mathbf{i}$-component of the curl is:",
    options: ["$-2z$", "$2z$", "$0$"],
    answer: "A",
    explanation: "$R_y - Q_z = 0 - 2z = -2z$.",
  }
];

export const VECTOR_CATALOG_QUIZ = [
  {
    prompt: "Work of $\\mathbf F$ along $C$ is the line integral:",
    options: ["$\\int_C\\mathbf F\\cdot d\\mathbf r$", "$\\int_C\\mathbf F\\cdot\\mathbf n\\,ds$", "$\\iint_S\\mathbf F\\cdot\\mathbf n\\,dS$"],
    answer: "A",
    explanation: "Tangent work integral.",
  },
  {
    prompt: "A field is conservative on a simply connected domain if:",
    options: ["$\\|\\mathbf F\\|=1$", "$\\nabla\\times\\mathbf F=\\mathbf 0$", "$\\nabla\\cdot\\mathbf F=0$"],
    answer: "B",
    explanation: "Irrotational plus topology hypotheses.",
  },
  {
    prompt: "For $\\mathbf F=\\langle 2x,2y\\rangle$, a potential is:",
    options: ["$f=x+y$", "$f=2xy$", "$f=x^2+y^2$"],
    answer: "C",
    explanation: "$\\nabla f=\\langle 2x,2y\\rangle$.",
  },
  {
    prompt: "Path independence of work integrals is equivalent to:",
    options: ["Zero divergence", "Closed paths only", "Existence of a scalar potential (on the domain)"],
    answer: "C",
    explanation: "Conservative field characterization.",
  },
  {
    prompt: "Flux of a 2D field across a curve uses:",
    options: ["$\\int_C\\mathbf F\\cdot d\\mathbf r$", "$\\int_C\\mathbf F\\cdot\\mathbf n\\,ds$", "$\\nabla\\times\\mathbf F$ only"],
    answer: "B",
    explanation: "Normal line integral.",
  },
  {
    prompt: "Test: for $\\langle P,Q\\rangle=\\langle -y,x\\rangle$, $Q_x-P_y$ equals $2\\ne 0$, so on $\\mathbb{R}^2$ the field is:",
    options: ["Not conservative", "Divergence-free and conservative", "Conservative with potential $xy$"],
    answer: "A",
    explanation: "Nonzero curl obstruction.",
  },
  {
    prompt: "Physical reading: divergence models local:",
    options: ["Expansion/compression of fluid", "Rotation rate only", "Temperature alone"],
    answer: "A",
    explanation: "Continuity equation intuition.",
  },
  {
    prompt: "Physical reading: curl models local:",
    options: ["Mass creation only", "Rotation / vorticity", "Pressure"],
    answer: "B",
    explanation: "Vorticity interpretation.",
  },
  {
    prompt: "Find $f$ with $\\nabla f=\\langle y,x\\rangle$:",
    options: ["$f=x+y$", "$f=x^2+y^2$", "$f=xy+C$"],
    answer: "C",
    explanation: "Integrate $f_x=y$ then match $f_y=x$.",
  },
  {
    prompt: "If $\\mathbf F=\\nabla f$ and $A,B$ are endpoints of any path in a simply connected domain,",
    options: ["The integral depends on the path shape", "The integral is always $0$", "$\\int_A^B\\mathbf F\\cdot d\\mathbf r=f(B)-f(A)$"],
    answer: "C",
    explanation: "Fundamental theorem for line integrals.",
  },
  {
    prompt: "Mixed partials test for $\\langle P,Q\\rangle$: check:",
    options: ["$P+Q=0$", "$P_y=Q_x$", "$P_x=Q_y$"],
    answer: "B",
    explanation: "Necessary condition for a potential.",
  },
  {
    prompt: "For $\\mathbf F=\\langle e^y,xe^y\\rangle$, since $P_y=e^y=Q_x$, a potential is:",
    options: ["$f=xe^y$", "$f=xy$", "$f=e^y$"],
    answer: "A",
    explanation: "Integrate $P$ in $x$.",
  },
  {
    prompt: "Circulation around a closed curve vanishes for every closed curve when:",
    options: ["The curve is a circle", "Divergence is zero", "The field is conservative"],
    answer: "C",
    explanation: "Equivalent to path independence.",
  },
  {
    prompt: "Unit tangent work with $\\mathbf F=\\langle 1,0\\rangle$ along the segment from $(0,0)$ to $(2,0)$ equals:",
    options: ["$2$", "$1$", "$0$"],
    answer: "A",
    explanation: "$\\int_0^2 1\\,dx=2$.",
  },
  {
    prompt: "If a potential exists, curl of $\\mathbf F$ must be:",
    options: ["Nonzero", "$\\mathbf 0$", "Equal to $\\mathbf F$"],
    answer: "B",
    explanation: "Necessary condition.",
  },
  {
    prompt: "A vector field is formally defined as solenoidal if:",
    options: ["$\\nabla \\cdot \\mathbf F = 0$", "$\\nabla \\times \\mathbf F = \\mathbf 0$", "$\\mathbf F = \\nabla f$"],
    answer: "A",
    explanation: "Solenoidal means the field behaves like an incompressible fluid with zero divergence.",
  },
  {
    prompt: "A vector field is formally defined as irrotational if:",
    options: ["$\\nabla \\times \\mathbf F = \\mathbf 0$", "$\\nabla \\cdot \\mathbf F = 0$", "$\\mathbf F = \\mathbf 0$"],
    answer: "A",
    explanation: "Irrotational means there is no local spinning, hence zero curl.",
  },
  {
    prompt: "The 2D field $\\mathbf F = \\langle 2xy, x^2 \\rangle$ is:",
    options: ["Conservative", "Not conservative", "Solenoidal"],
    answer: "A",
    explanation: "Since $P_y = 2x$ and $Q_x = 2x$, the curl is zero, making it conservative.",
  },
  {
    prompt: "The total work done by a conservative vector field moving a particle around any closed loop is:",
    options: ["Always zero", "Equal to the loop's area", "Dependent on the path length"],
    answer: "A",
    explanation: "Path independence implies that returning to the starting point yields zero net work.",
  },
  {
    prompt: "Which of the following is a potential function for $\\mathbf F = \\langle 1, 1, 1 \\rangle$?",
    options: ["$f(x,y,z) = x+y+z$", "$f(x,y,z) = xyz$", "$f(x,y,z) = x^2+y^2+z^2$"],
    answer: "A",
    explanation: "Taking the gradient of $x+y+z$ gives exactly $\\langle 1, 1, 1 \\rangle$.",
  }
];

export const VECTOR_IDENTITY_QUIZ = [
  {
    prompt: "Identity: $\\nabla\\times(\\nabla f)$ equals:",
    options: ["$\\mathbf 0$", "$\\nabla^2 f$", "$f$"],
    answer: "A",
    explanation: "Curl of a gradient vanishes.",
  },
  {
    prompt: "Identity: $\\nabla\\cdot(\\nabla\\times\\mathbf F)$ equals:",
    options: ["$\\|\\nabla\\times\\mathbf F\\|$", "$\\nabla\\cdot\\mathbf F$", "$0$"],
    answer: "C",
    explanation: "Divergence of a curl vanishes.",
  },
  {
    prompt: "Product rule: $\\nabla\\cdot(f\\mathbf F)$ expands to:",
    options: ["$f\\nabla\\times\\mathbf F$", "$f\\nabla\\cdot\\mathbf F+\\nabla f\\cdot\\mathbf F$", "$\\nabla f\\times\\mathbf F$"],
    answer: "B",
    explanation: "Standard identity.",
  },
  {
    prompt: "For $f=xyz$ and $\\mathbf F=\\langle 1,0,0\\rangle$, $\\nabla\\cdot(f\\mathbf F)$ equals:",
    options: ["$0$", "$yz$", "$xy$"],
    answer: "B",
    explanation: "$f\\nabla\\cdot\\mathbf F+\\nabla f\\cdot\\mathbf F=0+\\langle yz,xz,xy\\rangle\\cdot\\langle 1,0,0\\rangle=yz$.",
  },
  {
    prompt: "Laplacian $\\nabla^2 f$ in Cartesian 3D is:",
    options: ["$\\|\\nabla f\\|^2$", "$f_x+f_y+f_z$", "$f_{xx}+f_{yy}+f_{zz}$"],
    answer: "C",
    explanation: "Definition.",
  },
  {
    prompt: "Compute $\\nabla^2(x^2+y^2+z^2)$.",
    options: ["$6$", "$2$", "$0$"],
    answer: "A",
    explanation: "Second derivatives sum to $6$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle -y,x,0\\rangle$, $\\nabla\\cdot(\\nabla\\times\\mathbf F)$ equals:",
    options: ["$-2$", "$2$", "$0$"],
    answer: "C",
    explanation: "Identity forces zero.",
  },
  {
    prompt: "Expand $\\nabla\\times(f\\mathbf F)$ schematically includes:",
    options: ["$f(\\nabla\\times\\mathbf F)+\\nabla f\\times\\mathbf F$", "Only $\\nabla^2 f$", "Only $f\\nabla\\cdot\\mathbf F$"],
    answer: "A",
    explanation: "Curl product rule.",
  },
  {
    prompt: "If $\\mathbf F=\\nabla\\times\\mathbf A$, then automatically $\\nabla\\cdot\\mathbf F$ equals:",
    options: ["$\\|\\mathbf A\\|$", "$0$", "$\\nabla\\times\\mathbf A$"],
    answer: "B",
    explanation: "Divergence of a curl.",
  },
  {
    prompt: "Surface element on graph $z=g(x,y)$ uses the vector:",
    options: ["$\\langle 1,0,0\\rangle\\,dx\\,dy$", "$\\langle -g_x,-g_y,1\\rangle\\,dx\\,dy$", "$\\langle g_x,g_y,0\\rangle$"],
    answer: "B",
    explanation: "Upward graph orientation.",
  },
  {
    prompt: "For $f=e^{x}$, $\\nabla^2 f$ equals:",
    options: ["$0$", "$2e^{x}$", "$e^{x}$"],
    answer: "C",
    explanation: "Only $f_{xx}=e^x$.",
  },
  {
    prompt: "Verify $\\nabla\\times\\langle y,z,x\\rangle$ then take divergence: result is:",
    options: ["$0$", "$1$", "$3$"],
    answer: "A",
    explanation: "Must be zero by identity.",
  },
  {
    prompt: "Harmonic functions satisfy $\\nabla^2 f=0$. Is $f=x$ harmonic?",
    options: ["Only on the unit ball", "No, because $f_{xx}=1$", "Yes"],
    answer: "C",
    explanation: "All second derivatives vanish.",
  },
  {
    prompt: "Parameterized surface $\\mathbf r(u,v)$ has normal factor:",
    options: ["$\\mathbf r_u\\times\\mathbf r_v$", "$\\mathbf r_u\\cdot\\mathbf r_v$", "$\\|\\mathbf r\\|$"],
    answer: "A",
    explanation: "Standard surface Jacobian vector.",
  },
  {
    prompt: "If $\\nabla\\times\\mathbf F=\\mathbf 0$ and $\\nabla\\cdot\\mathbf F=0$, then in Cartesian coordinates $F$ is both:",
    options: ["Necessarily constant", "Irrotational and solenoidal", "Only conservative in name"],
    answer: "B",
    explanation: "Both differential conditions.",
  },
  {
    prompt: "The vector identity $\\nabla \\cdot (\\mathbf F \\times \\mathbf G)$ expands to:",
    options: ["$\\mathbf G \\cdot (\\nabla \\times \\mathbf F) - \\mathbf F \\cdot (\\nabla \\times \\mathbf G)$", "$\\mathbf F \\cdot (\\nabla \\times \\mathbf G) + \\mathbf G \\cdot (\\nabla \\times \\mathbf F)$", "$\\mathbf 0$"],
    answer: "A",
    explanation: "This is the standard product rule for the divergence of a cross product.",
  },
  {
    prompt: "The gradient of a product of two scalar functions, $\\nabla(fg)$, expands as:",
    options: ["$f\\nabla g + g\\nabla f$", "$f\\nabla g - g\\nabla f$", "$\\nabla f \\cdot \\nabla g$"],
    answer: "A",
    explanation: "It follows the standard product rule of calculus for each partial derivative.",
  },
  {
    prompt: "Is the statement $\\nabla \\cdot (\\nabla f) = 0$ always true?",
    options: ["No, it equals the Laplacian $\\nabla^2 f$", "Yes, divergence of a gradient is always zero", "Only if $f$ is a constant"],
    answer: "A",
    explanation: "The divergence of a gradient forms the Laplacian, which is only zero for harmonic functions.",
  },
  {
    prompt: "The curl of any constant vector field $\\mathbf F = \\langle a, b, c \\rangle$ is:",
    options: ["$\\mathbf 0$", "$\\langle a, b, c \\rangle$", "Undefined"],
    answer: "A",
    explanation: "Since the components are constants, all their partial derivatives are exactly zero.",
  },
  {
    prompt: "The vector identity $\\nabla \\times (\\nabla f) = \\mathbf{0}$ proves that:",
    options: ["Conservative fields are irrotational", "Solenoidal fields are incompressible", "Gradients have constant divergence"],
    answer: "A",
    explanation: "Because the curl of a gradient is always zero, a conservative field (which is a gradient) must have zero curl (be irrotational).",
  }
];

export const DIV_THEOREM_QUIZ = [
  {
    prompt: "Divergence theorem equates $\\iint_S\\mathbf F\\cdot\\mathbf n\\,dS$ with:",
    options: ["$\\iint_S(\\nabla\\times\\mathbf F)\\cdot\\mathbf n\\,dS$", "$\\oint_C\\mathbf F\\cdot d\\mathbf r$", "$\\iiint_E\\nabla\\cdot\\mathbf F\\,dV$"],
    answer: "C",
    explanation: "Flux equals triple integral of divergence.",
  },
  {
    prompt: "Hypotheses require $S$ to be:",
    options: ["Closed and oriented outward", "An open disk only", "A graph over $xy$ only"],
    answer: "A",
    explanation: "Closed surface with outward normal.",
  },
  {
    prompt: "For $\\mathbf F=\\langle x,y,z\\rangle$ on the unit sphere, flux equals:",
    options: ["$0$", "$4\\pi$", "$4\\pi/3$"],
    answer: "B",
    explanation: "$\\nabla\\cdot\\mathbf F=3$, volume $4\\pi/3$, product $4\\pi$.",
  },
  {
    prompt: "Flux of $\\mathbf F=\\langle x,y,z\\rangle$ through the sphere of radius $a$ equals:",
    options: ["$4\\pi a^2$", "$4\\pi a^3$", "$\\dfrac{4}{3}\\pi a^3$"],
    answer: "B",
    explanation: "$\\nabla\\cdot\\mathbf F=3$ and volume $\\dfrac{4}{3}\\pi a^3$ give $4\\pi a^3$.",
  },
  {
    prompt: "If $\\nabla\\cdot\\mathbf F=0$ in $E$, flux through $\\partial E$ is:",
    options: ["$0$", "Equal to surface area", "$1$"],
    answer: "A",
    explanation: "Triple integral of zero.",
  },
  {
    prompt: "Box $[0,1]^3$ and $\\mathbf F=\\langle x,0,0\\rangle$: flux outward equals:",
    options: ["$3$", "$0$", "$1$"],
    answer: "C",
    explanation: "$\\nabla\\cdot\\mathbf F=1$, volume $1$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle 0,0,z\\rangle$ on the unit ball, flux equals:",
    options: ["$0$", "$4\\pi/3$", "$2\\pi$"],
    answer: "B",
    explanation: "Divergence $1$ times volume $4\\pi/3$.",
  },
  {
    prompt: "Outward flux through two closed surfaces bounding a shell equals the triple integral of divergence over:",
    options: ["The region between them", "All of $\\mathbb{R}^3$", "Only the inner cavity"],
    answer: "A",
    explanation: "Apply the theorem to the shell region.",
  },
  {
    prompt: "Compute outward flux of $\\langle 2x,3y,-5z\\rangle$ through any closed surface enclosing volume $V$:",
    options: ["$10V$", "$V$", "$0$"],
    answer: "C",
    explanation: "Divergence $2+3-5=0$.",
  },
  {
    prompt: "Unit cube and $\\mathbf F=\\langle xy,0,0\\rangle$: $\\nabla\\cdot\\mathbf F=y$, flux equals:",
    options: ["$1$", "$1/2$", "$0$"],
    answer: "B",
    explanation: "$\\int_0^1\\int_0^1\\int_0^1 y\\,dx\\,dy\\,dz=1/2$.",
  },
  {
    prompt: "Gauss theorem fails for the Coulomb field through a sphere about the origin because:",
    options: ["Divergence is zero so flux must be zero anyway", "The sphere is not closed", "The field is not $C^1$ at the origin inside"],
    answer: "C",
    explanation: "Singularity at the origin violates hypotheses.",
  },
  {
    prompt: "Cylinder $x^2+y^2\\le 1$, $0\\le z\\le 1$, field $\\langle 0,0,1\\rangle$: outward flux equals:",
    options: ["$0$", "$2\\pi$", "$\\pi$"],
    answer: "A",
    explanation: "Divergence is $0$, so the triple integral vanishes.",
  },
  {
    prompt: "Same cylinder, field $\\langle 0,0,z\\rangle$: outward flux equals:",
    options: ["$0$", "$1$", "$\\pi$"],
    answer: "C",
    explanation: "Divergence $1$, volume $\\pi$.",
  },
  {
    prompt: "If flux out of every closed surface is zero, then (smooth case) $\\nabla\\cdot\\mathbf F$ is:",
    options: ["Identically $1$", "Identically $0$", "Equal to curl"],
    answer: "B",
    explanation: "Localize the triple integral.",
  },
  {
    prompt: "Orientation: divergence theorem uses the:",
    options: ["Outward unit normal", "Tangent to every curve", "Inward normal only"],
    answer: "A",
    explanation: "Standard orientation convention.",
  },
  {
    prompt: "The Divergence Theorem requires the solid region $E$ to be:",
    options: ["Bounded with a piecewise smooth, closed boundary", "An infinite plane", "An open surface with a boundary curve"],
    answer: "A",
    explanation: "Gauss's Theorem applies to bounded 3D volumes completely enclosed by a surface.",
  },
  {
    prompt: "Using the Divergence Theorem, the flux of $\\mathbf F = \\langle 3x, 3y, 3z \\rangle$ out of a region of volume $V$ is:",
    options: ["$9V$", "$3V$", "$0$"],
    answer: "A",
    explanation: "$\\nabla \\cdot \\mathbf F = 3 + 3 + 3 = 9$. The triple integral of $9$ over volume $V$ is $9V$.",
  },
  {
    prompt: "By the Divergence Theorem, the outward flux of a curl field $\\nabla \\times \\mathbf F$ through any closed surface is:",
    options: ["Always zero", "Equal to the enclosed volume", "Equal to the surface area"],
    answer: "A",
    explanation: "Since $\\nabla \\cdot (\\nabla \\times \\mathbf F) = 0$, the triple integral of the divergence is strictly zero.",
  },
  {
    prompt: "For the vector field $\\mathbf F = \\langle x^2, 0, 0 \\rangle$, the divergence is:",
    options: ["$2x$", "$x^2$", "$0$"],
    answer: "A",
    explanation: "Only the $x$-component has a non-zero partial derivative: $P_x = 2x$.",
  },
  {
    prompt: "If a fluid is totally incompressible ($\\nabla \\cdot \\mathbf F = 0$), the net flux through any closed surface is:",
    options: ["$0$", "Positive", "Negative"],
    answer: "A",
    explanation: "The Divergence Theorem states the flux equals the integral of divergence. If divergence is zero, the flux is zero.",
  }
];

export const STOKES_THEOREM_QUIZ = [
  {
    prompt: "Stokes equates $\\oint_C\\mathbf F\\cdot d\\mathbf r$ with:",
    options: ["$\\iiint\\nabla\\cdot\\mathbf F\\,dV$", "$\\iint_S(\\nabla\\times\\mathbf F)\\cdot\\mathbf n\\,dS$", "$\\iint_S\\mathbf F\\cdot\\mathbf n\\,dS$"],
    answer: "B",
    explanation: "Circulation equals curl flux.",
  },
  {
    prompt: "Green's theorem is Stokes for a flat region in the $xy$-plane with normal:",
    options: ["$-\\mathbf j$", "$\\mathbf i$", "$\\mathbf k$"],
    answer: "C",
    explanation: "Flat case.",
  },
  {
    prompt: "If $S_1$ and $S_2$ share oriented boundary $C$, curl fluxes through them:",
    options: ["Are equal", "Differ by volume enclosed", "Must both vanish"],
    answer: "A",
    explanation: "Both equal the same circulation.",
  },
  {
    prompt: "For $\\mathbf F=\\langle -y,x,0\\rangle$ on the unit circle, Stokes on the disk yields circulation:",
    options: ["$2\\pi$", "$0$", "$\\pi$"],
    answer: "A",
    explanation: "Curl $2\\mathbf k$; area $\\pi$.",
  },
  {
    prompt: "Orientation: right-hand rule ties:",
    options: ["Divergence to curl", "Only arc length to flux", "Boundary direction to the chosen normal"],
    answer: "C",
    explanation: "Consistent orientation.",
  },
  {
    prompt: "Curl flux through a closed surface (no boundary) is:",
    options: ["Equal to enclosed volume", "$0$", "$4\\pi$"],
    answer: "B",
    explanation: "Boundary empty $\\Rightarrow$ Stokes gives $0$; also div(curl)=0.",
  },
  {
    prompt: "Replacing the upper hemisphere by the equatorial disk for Stokes is valid because:",
    options: ["They have equal area", "They share the same oriented boundary circle", "Curl is zero"],
    answer: "B",
    explanation: "Shared boundary.",
  },
  {
    prompt: "If $\\nabla\\times\\mathbf F=\\mathbf 0$, circulation on any $C=\\partial S$ is:",
    options: ["$0$", "Path-dependent", "$1$"],
    answer: "A",
    explanation: "Stokes integral of zero.",
  },
  {
    prompt: "For $\\mathbf F=\\langle z,x,y\\rangle$, $\\nabla\\times\\mathbf F=\\langle 1,1,1\\rangle$. Curl flux through unit disk $z=0$ upward:",
    options: ["$1$", "$0$", "$\\pi$"],
    answer: "C",
    explanation: "Only $z$-component contributes: area $\\pi$.",
  },
  {
    prompt: "Green integrand $Q_x-P_y$ for $\\langle -y,x\\rangle$ equals:",
    options: ["$0$", "$1$", "$2$"],
    answer: "C",
    explanation: "$1-(-1)=2$.",
  },
  {
    prompt: "Stokes requires $\\mathbf F$ to be:",
    options: ["Polynomial", "$C^1$ near the surface", "Only continuous"],
    answer: "B",
    explanation: "Need continuous partials to form curl.",
  },
  {
    prompt: "Circulation of a conservative field on a closed curve is:",
    options: ["$0$", "Equal to $\\|\\mathbf F\\|$", "Nonzero in general"],
    answer: "A",
    explanation: "Fundamental theorem / Stokes.",
  },
  {
    prompt: "If two surfaces have opposite normals on the same geometric set with the same rim, Stokes integrals:",
    options: ["Are unrelated", "Differ by a sign", "Both double"],
    answer: "B",
    explanation: "Orientation reversal.",
  },
  {
    prompt: "Ampere-type reading: circulation of $\\mathbf B$ relates to flux of:",
    options: ["A curl-like current density through a spanning surface", "Scalar potential alone", "Only $\\nabla\\cdot\\mathbf E$"],
    answer: "A",
    explanation: "Stokes-type physical law.",
  },
  {
    prompt: "For the unit circle as boundary, the paraboloid $z=1-x^2-y^2$ ($z\\ge 0$) is interchangeable with:",
    options: ["The plane $z=2$", "The sphere of radius $2$", "The unit disk in $z=0$"],
    answer: "C",
    explanation: "Same rim $x^2+y^2=1$, $z=0$.",
  },
  {
    prompt: "Stokes' Theorem can be viewed as the 3D generalization of which 2D theorem?",
    options: ["Green's Theorem", "The Divergence Theorem", "Fundamental Theorem of Calculus"],
    answer: "A",
    explanation: "Green's Theorem is exactly Stokes' Theorem applied to a flat surface in the $xy$-plane.",
  },
  {
    prompt: "If the surface $S$ is a completely closed sphere, its boundary curve $C$ is:",
    options: ["Empty, so the circulation integral is zero", "The equator of the sphere", "A point at the pole"],
    answer: "A",
    explanation: "A closed surface has no edge. With no boundary curve, the line integral evaluates to zero.",
  },
  {
    prompt: "The orientation of the surface $S$ and its boundary curve $C$ must be consistent according to:",
    options: ["The right-hand rule", "The left-hand rule", "The outward-normal rule only"],
    answer: "A",
    explanation: "If the thumb of your right hand points in the direction of the normal vector, your fingers curl in the positive direction of curve $C$.",
  },
  {
    prompt: "To evaluate the circulation of $\\mathbf F$ along a boundary $C$, you compute the line integral:",
    options: ["$\\oint_C \\mathbf F \\cdot d\\mathbf r$", "$\\oint_C \\mathbf F \\cdot \\mathbf n \\, ds$", "$\\iint_S \\mathbf F \\cdot d\\mathbf S$"],
    answer: "A",
    explanation: "Circulation is the tangential line integral of the field around a closed curve.",
  },
  {
    prompt: "If $S_1$ is a hemisphere and $S_2$ is a flat disk, and both share the exact same boundary curve $C$, then their curl fluxes $\\iint \\nabla \\times \\mathbf F \\cdot d\\mathbf S$ are:",
    options: ["Equal to each other", "Zero", "Proportional to their respective surface areas"],
    answer: "A",
    explanation: "By Stokes' Theorem, both surface integrals equal the exact same line integral around their shared boundary $C$.",
  }
];

export const DIVCURL_CHALLENGE_QUIZ = [
  {
    prompt: "Flux of $\\langle x,y,z\\rangle$ out of the sphere $x^2+y^2+z^2=4$ equals:",
    options: ["$32\\pi$", "$16\\pi$", "$4\\pi$"],
    answer: "A",
    explanation: "Radius $2$, volume $\\tfrac{32}{3}\\pi$, times $3$ gives $32\\pi$.",
  },
  {
    prompt: "Circulation of $\\langle -y,x,0\\rangle$ around $x^2+y^2=9$, $z=0$, via the disk:",
    options: ["$6\\pi$", "$9\\pi$", "$18\\pi$"],
    answer: "C",
    explanation: "Curl $2\\mathbf k$; area $9\\pi$; product $18\\pi$.",
  },
  {
    prompt: "Cheapest tool for outward flux of a divergence-free field through a closed surface:",
    options: ["Parametrize the whole surface", "Conclude the flux is $0$ immediately", "Convert to Stokes"],
    answer: "B",
    explanation: "Divergence theorem with zero integrand.",
  },
  {
    prompt: "Field $\\langle -y/(x^2+y^2),x/(x^2+y^2),0\\rangle$ on the unit circle has circulation $2\\pi$ even though curl vanishes off the origin because:",
    options: ["Divergence theorem applies instead", "The singularity at the origin blocks a naive Stokes application on the disk", "The field is conservative on $\\mathbb{R}^2$"],
    answer: "B",
    explanation: "Classic counterexample domain issue.",
  },
  {
    prompt: "Compute $\\nabla\\times\\langle yz,xz,xy\\rangle$ then $\\nabla\\cdot$ of that curl:",
    options: ["$xyz$", "$1$", "$0$"],
    answer: "C",
    explanation: "Identity.",
  },
  {
    prompt: "Outward flux of $\\langle 4x,y,-5z\\rangle$ through the unit sphere:",
    options: ["$0$", "$4\\pi/3$", "$4\\pi$"],
    answer: "A",
    explanation: "Divergence $4+1-5=0$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle 0,-z,y\\rangle$, circulation around the unit circle in the $yz$-plane via Stokes on the unit disk in $x=0$:",
    options: ["$0$", "$\\pi$", "$2\\pi$"],
    answer: "C",
    explanation: "Curl $\\langle 2,0,0\\rangle$; flux through disk normal $\\mathbf i$ is $2\\pi$.",
  },
  {
    prompt: "Maxwell-style: if $\\mathbf B=\\nabla\\times\\mathbf A$, then $\\nabla\\cdot\\mathbf B$ equals:",
    options: ["$\\nabla\\cdot\\mathbf A$", "$0$", "$\\|\\mathbf A\\|$"],
    answer: "B",
    explanation: "Divergence of curl.",
  },
  {
    prompt: "Volume of the region enclosed equals flux of $\\tfrac13\\langle x,y,z\\rangle$ because:",
    options: ["Divergence is $1$", "The field is constant", "Curl is $\\mathbf 0$"],
    answer: "A",
    explanation: "$\\nabla\\cdot(\\tfrac13\\langle x,y,z\\rangle)=1$.",
  },
  {
    prompt: "Flux of $\\langle x^3,y^3,z^3\\rangle$ out of the unit ball: use divergence $3(x^2+y^2+z^2)$ and symmetry — the integral equals:",
    options: ["$4\\pi/3$", "$0$", "$12\\pi/5$"],
    answer: "C",
    explanation: "In spherical coordinates one obtains $12\\pi/5$.",
  },
  {
    prompt: "Decide tool: compute $\\oint_C\\langle -y,x,z\\rangle\\cdot d\\mathbf r$ for $C$ the unit circle $z=0$. Best first step:",
    options: ["Apply Stokes on the unit disk", "Parametrize a helix", "Use divergence theorem on a ball"],
    answer: "A",
    explanation: "Closed curve in a plane — Stokes is natural.",
  },
  {
    prompt: "That circulation equals:",
    options: ["$\\pi$", "$2\\pi$", "$0$"],
    answer: "B",
    explanation: "Curl $\\langle 0,0,2\\rangle$; flux $2\\pi$.",
  },
  {
    prompt: "If $\\nabla\\cdot\\mathbf F=2$ constantly, flux out of a ball of radius $1$ is:",
    options: ["$8\\pi/3$", "$4\\pi/3$", "$2$"],
    answer: "A",
    explanation: "$2\\times$ volume $4\\pi/3$.",
  },
  {
    prompt: "For $\\mathbf F=\\langle 2xy,x^2,0\\rangle$, curl is $\\mathbf 0$ and a potential is:",
    options: ["$f=xy$", "$f=x^2y$", "$f=x^2+y$"],
    answer: "B",
    explanation: "$\\nabla(x^2y)=\\langle 2xy,x^2,0\\rangle$.",
  },
  {
    prompt: "Closed-surface flux of $\\nabla\\times\\mathbf G$ for smooth $\\mathbf G$ is:",
    options: ["The enclosed volume", "Equal to $\\|\\mathbf G\\|$", "Always $0$"],
    answer: "C",
    explanation: "div(curl)=0 or Stokes with empty boundary.",
  },
  {
    prompt: "Let $\\mathbf F = \\mathbf r / |\\mathbf r|^3$ be the inverse-square field. The flux through a sphere centered at the origin is $4\\pi$. Why doesn't the Divergence Theorem yield $0$?",
    options: ["The field is undefined at the origin, violating the theorem's requirements", "The divergence of this field is actually $4\\pi$ everywhere", "The sphere is not a closed surface"],
    answer: "A",
    explanation: "The singularity at $(0,0,0)$ means the region is not continuous, so you cannot simply integrate divergence over the solid ball.",
  },
  {
    prompt: "Find the outward flux of $\\mathbf F = \\langle x, y, z \\rangle$ through a cube of side length $2$.",
    options: ["$24$", "$8$", "$0$"],
    answer: "A",
    explanation: "The divergence is $3$. The volume of a $2 \\times 2 \\times 2$ cube is $8$. Flux = $3 \\times 8 = 24$.",
  },
  {
    prompt: "What is $\\nabla \\cdot (\\mathbf r)$ where $\\mathbf r = \\langle x, y, z \\rangle$ is the position vector?",
    options: ["$3$", "$1$", "$\\langle 1, 1, 1 \\rangle$"],
    answer: "A",
    explanation: "The partial derivatives are $\\partial x/\\partial x = 1$, $\\partial y/\\partial y = 1$, $\\partial z/\\partial z = 1$, summing to $3$.",
  },
  {
    prompt: "If $\\mathbf F = \\nabla(\\ln(x^2+y^2))$, what is $\\nabla \\times \\mathbf F$?",
    options: ["$\\mathbf 0$", "$\\langle 0, 0, 2 \\rangle$", "$\\langle 1/x, 1/y, 0 \\rangle$"],
    answer: "A",
    explanation: "The field is explicitly given as the gradient of a scalar function, so its curl must be identically zero.",
  },
  {
    prompt: "A field has a constant divergence of $C$. The flux out of a sphere of radius $R$ is:",
    options: ["$C \\frac{4}{3}\\pi R^3$", "$C 4\\pi R^2$", "$C \\pi R^2$"],
    answer: "A",
    explanation: "Flux = $\\iiint C \\, dV = C \\times \\text{Volume of Sphere} = C (\\frac{4}{3}\\pi R^3)$.",
  }
];
